import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function CaliforniaToIreland() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-[#0A381F] mb-6">
              Moving to Ireland from California: Complete 2024 Guide
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-8">
              Everything Californians need to know about relocating from CA to Ireland. Compare costs, weather, jobs, and lifestyle between the Golden State and the Emerald Isle.
            </p>
            <img 
              src="https://public.readdy.ai/ai/img_res/cd8290cae545a2b5873d04d32cdb216e.jpg"
              alt="Moving from California to Ireland comparison"
              className="w-full h-96 object-cover rounded-lg shadow-lg"
            />
          </div>

          {/* Quick Stats */}
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            <div className="bg-[#0A381F] text-white p-6 rounded-lg text-center">
              <i className="ri-sun-line text-4xl text-[#D4AF37] mb-3"></i>
              <h3 className="text-lg font-bold mb-2">Weather</h3>
              <p className="text-sm">Cooler & Rainier</p>
            </div>
            <div className="bg-[#0A381F] text-white p-6 rounded-lg text-center">
              <i className="ri-home-line text-4xl text-[#D4AF37] mb-3"></i>
              <h3 className="text-lg font-bold mb-2">Housing</h3>
              <p className="text-sm">Similar to SF/LA</p>
            </div>
            <div className="bg-[#0A381F] text-white p-6 rounded-lg text-center">
              <i className="ri-briefcase-line text-4xl text-[#D4AF37] mb-3"></i>
              <h3 className="text-lg font-bold mb-2">Tech Jobs</h3>
              <p className="text-sm">Google, Apple, Meta</p>
            </div>
            <div className="bg-[#0A381F] text-white p-6 rounded-lg text-center">
              <i className="ri-flight-takeoff-line text-4xl text-[#D4AF37] mb-3"></i>
              <h3 className="text-lg font-bold mb-2">Flight Time</h3>
              <p className="text-sm">10-11 hours direct</p>
            </div>
          </div>

          {/* Why Californians Move */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-[#0A381F] mb-8">Why Californians Are Choosing Ireland</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-2xl font-bold text-[#0A381F] mb-4">
                  <i className="ri-building-line text-[#D4AF37] mr-2"></i>
                  Same Tech Companies, Lower Cost
                </h3>
                <p className="text-gray-700 mb-4">
                  All the major Silicon Valley companies have offices in Ireland: Google, Apple, Meta, LinkedIn, Salesforce. Californians can transfer internally or find similar roles with competitive salaries but much lower living costs than SF or LA.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>Google's European HQ is in Dublin</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>Apple's European operations in Cork</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>Meta, LinkedIn, Salesforce all in Dublin</span>
                  </li>
                </ul>
              </div>
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-2xl font-bold text-[#0A381F] mb-4">
                  <i className="ri-heart-pulse-line text-[#D4AF37] mr-2"></i>
                  Affordable Healthcare
                </h3>
                <p className="text-gray-700 mb-4">
                  Californians pay some of the highest healthcare costs in America. In Ireland, healthcare is dramatically cheaper - even with private insurance, you'll pay 70-80% less than California's Covered California or employer plans.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>Private insurance: €1,000-2,500/year vs $6,000-12,000 in CA</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>No medical bankruptcy risk</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>Prescription drugs cost fraction of CA prices</span>
                  </li>
                </ul>
              </div>
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-2xl font-bold text-[#0A381F] mb-4">
                  <i className="ri-earth-line text-[#D4AF37] mr-2"></i>
                  European Adventure Base
                </h3>
                <p className="text-gray-700 mb-4">
                  Californians love to travel, and Ireland is the perfect European base. Weekend trips to Paris, London, Barcelona, Rome, or Amsterdam cost $50-150 roundtrip. You'll explore more of Europe in one year than most Californians do in a lifetime.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>2-hour flights to most European cities</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>Budget airlines for $50 weekend trips</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>30+ countries within 3 hours</span>
                  </li>
                </ul>
              </div>
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-2xl font-bold text-[#0A381F] mb-4">
                  <i className="ri-leaf-line text-[#D4AF37] mr-2"></i>
                  Green & Beautiful Nature
                </h3>
                <p className="text-gray-700 mb-4">
                  While California battles droughts and wildfires, Ireland is lush and green year-round. Californians who love nature find Ireland's landscapes stunning - dramatic coastlines, rolling hills, and no wildfire smoke.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>No drought or water restrictions</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>No wildfire season or smoke</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>Stunning coastal scenery everywhere</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Cost Comparison */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-[#0A381F] mb-8">California vs Ireland: Cost Comparison</h2>
            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-3 text-left text-[#0A381F]">Expense</th>
                    <th className="border border-gray-300 px-4 py-3 text-left text-[#0A381F]">San Francisco</th>
                    <th className="border border-gray-300 px-4 py-3 text-left text-[#0A381F]">Los Angeles</th>
                    <th className="border border-gray-300 px-4 py-3 text-left text-[#0A381F]">Dublin</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr>
                    <td className="border border-gray-300 px-4 py-3 font-bold">1-Bed Apartment</td>
                    <td className="border border-gray-300 px-4 py-3">$3,000-4,500</td>
                    <td className="border border-gray-300 px-4 py-3">$2,200-3,500</td>
                    <td className="border border-gray-300 px-4 py-3">$1,980-2,750</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3 font-bold">2-Bed Apartment</td>
                    <td className="border border-gray-300 px-4 py-3">$4,500-6,500</td>
                    <td className="border border-gray-300 px-4 py-3">$3,200-5,000</td>
                    <td className="border border-gray-300 px-4 py-3">$2,420-3,520</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3 font-bold">Gasoline (per gallon)</td>
                    <td className="border border-gray-300 px-4 py-3">$4.50-5.50</td>
                    <td className="border border-gray-300 px-4 py-3">$4.50-5.50</td>
                    <td className="border border-gray-300 px-4 py-3">$7.15</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3 font-bold">Groceries (monthly)</td>
                    <td className="border border-gray-300 px-4 py-3">$500-700</td>
                    <td className="border border-gray-300 px-4 py-3">$450-650</td>
                    <td className="border border-gray-300 px-4 py-3">$400-600</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3 font-bold">Health Insurance</td>
                    <td className="border border-gray-300 px-4 py-3">$600-1,000/mo</td>
                    <td className="border border-gray-300 px-4 py-3">$500-900/mo</td>
                    <td className="border border-gray-300 px-4 py-3">$90-230/mo</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3 font-bold">Dinner for 2</td>
                    <td className="border border-gray-300 px-4 py-3">$80-120</td>
                    <td className="border border-gray-300 px-4 py-3">$70-100</td>
                    <td className="border border-gray-300 px-4 py-3">$66-99</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3 font-bold">State Income Tax</td>
                    <td className="border border-gray-300 px-4 py-3">Up to 13.3%</td>
                    <td className="border border-gray-300 px-4 py-3">Up to 13.3%</td>
                    <td className="border border-gray-300 px-4 py-3">No state tax</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6">
              <h3 className="text-xl font-bold text-[#0A381F] mb-3">
                <i className="ri-information-line text-blue-600 mr-2"></i>
                Important Note for Californians:
              </h3>
              <p className="text-gray-700">
                While Ireland has higher income tax rates than California (20-40% vs CA's 13.3% max), you save massively on healthcare, housing (compared to SF/LA), and have no state income tax. Most Californians find they have more disposable income in Ireland despite the tax difference.
              </p>
            </div>
          </section>

          {/* Weather Comparison */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-[#0A381F] mb-8">Weather: California vs Ireland</h2>
            <p className="text-lg text-gray-700 mb-8">
              This is the biggest adjustment for Californians! Ireland's weather is completely different from California's sunny climate. Here's what to expect:
            </p>
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <h3 className="text-2xl font-bold text-[#0A381F] mb-4">
                  <i className="ri-sun-line text-yellow-600 mr-2"></i>
                  California Weather
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li><strong>Summer:</strong> Hot and dry (75-95°F / 24-35°C)</li>
                  <li><strong>Winter:</strong> Mild (50-65°F / 10-18°C)</li>
                  <li><strong>Rain:</strong> Rare (mostly Nov-Mar)</li>
                  <li><strong>Sunshine:</strong> 280+ sunny days per year</li>
                  <li><strong>Humidity:</strong> Low and comfortable</li>
                  <li><strong>Challenges:</strong> Droughts, wildfires, heat waves</li>
                </ul>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <h3 className="text-2xl font-bold text-[#0A381F] mb-4">
                  <i className="ri-cloud-line text-green-600 mr-2"></i>
                  Ireland Weather
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li><strong>Summer:</strong> Mild (60-70°F / 15-21°C)</li>
                  <li><strong>Winter:</strong> Cool (40-50°F / 4-10°C)</li>
                  <li><strong>Rain:</strong> Frequent year-round (but light)</li>
                  <li><strong>Sunshine:</strong> 150-180 sunny days per year</li>
                  <li><strong>Humidity:</strong> Higher, but no extreme heat</li>
                  <li><strong>Benefits:</strong> No wildfires, no droughts, green year-round</li>
                </ul>
              </div>
            </div>
            <div className="bg-[#0A381F] text-white p-8 rounded-lg">
              <h3 className="text-2xl font-bold mb-4 text-[#D4AF37]">Weather Adjustment Tips for Californians:</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold mb-3">What to Bring:</h4>
                  <ul className="space-y-2">
                    <li>• Waterproof jacket (essential!)</li>
                    <li>• Layers - it's rarely hot or freezing</li>
                    <li>• Good umbrella</li>
                    <li>• Waterproof shoes/boots</li>
                    <li>• Vitamin D supplements (less sun)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold mb-3">What You Won't Need:</h4>
                  <ul className="space-y-2">
                    <li>• Air conditioning (rare in Ireland)</li>
                    <li>• Heavy winter coats (mild winters)</li>
                    <li>• Shorts and tank tops (rarely hot)</li>
                    <li>• Sunscreen daily (but bring for summer)</li>
                    <li>• Wildfire evacuation plan</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Jobs for Californians */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-[#0A381F] mb-8">Tech Jobs: Silicon Valley to Ireland</h2>
            <p className="text-lg text-gray-700 mb-8">
              Ireland is Europe's Silicon Valley! Many California tech workers transfer to Irish offices or find similar roles. The tech scene is vibrant, English-speaking, and offers great work-life balance.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-[#0A381F] mb-4">Bay Area → Dublin</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-building-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span><strong>Google:</strong> European HQ in Dublin</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-building-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span><strong>Meta:</strong> International HQ</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-building-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span><strong>LinkedIn:</strong> European HQ</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-building-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span><strong>Salesforce:</strong> Large Dublin office</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-building-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span><strong>Airbnb:</strong> European operations</span>
                  </li>
                </ul>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-[#0A381F] mb-4">Cupertino → Cork</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-building-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span><strong>Apple:</strong> Major Cork operations (6,000+ employees)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-building-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span><strong>Amazon:</strong> Cork office</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-building-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span><strong>VMware:</strong> Cork presence</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-building-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span><strong>EMC:</strong> Cork operations</span>
                  </li>
                </ul>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-[#0A381F] mb-4">Other CA Companies</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-building-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span><strong>Microsoft:</strong> Dublin office</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-building-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span><strong>Oracle:</strong> Dublin presence</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-building-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span><strong>PayPal:</strong> Dublin & Dundalk</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-building-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span><strong>eBay:</strong> Dublin office</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="bg-gradient-to-r from-[#0A381F] to-[#0d4a28] text-white p-8 rounded-lg">
              <h3 className="text-2xl font-bold mb-6 text-[#D4AF37]">Salary Comparison: California vs Ireland</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="font-bold mb-2">Software Engineer:</p>
                  <p>Bay Area: $150,000-250,000</p>
                  <p>Ireland: €70,000-120,000 ($77,000-132,000)</p>
                  <p className="text-sm text-[#D4AF37] mt-2">Lower salary but similar purchasing power due to lower costs</p>
                </div>
                <div>
                  <p className="font-bold mb-2">Product Manager:</p>
                  <p>Bay Area: $140,000-220,000</p>
                  <p>Ireland: €80,000-130,000 ($88,000-143,000)</p>
                  <p className="text-sm text-[#D4AF37] mt-2">Better work-life balance in Ireland</p>
                </div>
                <div>
                  <p className="font-bold mb-2">Data Scientist:</p>
                  <p>Bay Area: $130,000-200,000</p>
                  <p>Ireland: €70,000-110,000 ($77,000-121,000)</p>
                  <p className="text-sm text-[#D4AF37] mt-2">More vacation time (20-25 days vs 10-15)</p>
                </div>
                <div>
                  <p className="font-bold mb-2">UX Designer:</p>
                  <p>Bay Area: $110,000-170,000</p>
                  <p>Ireland: €60,000-95,000 ($66,000-104,500)</p>
                  <p className="text-sm text-[#D4AF37] mt-2">Shorter commutes, less stress</p>
                </div>
              </div>
            </div>
          </section>

          {/* Lifestyle Differences */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-[#0A381F] mb-8">Lifestyle: What Californians Notice</h2>
            <div className="space-y-6">
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-2xl font-bold text-[#0A381F] mb-4">
                  <i className="ri-car-line text-[#D4AF37] mr-2"></i>
                  Transportation & Commuting
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-bold text-gray-700 mb-2">California:</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li>• Car absolutely necessary</li>
                      <li>• Long commutes (60-90 min common)</li>
                      <li>• Traffic jams daily</li>
                      <li>• High gas and insurance costs</li>
                      <li>• Limited public transit</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-700 mb-2">Ireland:</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li>• Car optional in cities</li>
                      <li>• Short commutes (20-30 min average)</li>
                      <li>• Less traffic congestion</li>
                      <li>• Excellent public transport</li>
                      <li>• Walkable and bike-friendly cities</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-2xl font-bold text-[#0A381F] mb-4">
                  <i className="ri-restaurant-line text-[#D4AF37] mr-2"></i>
                  Food & Dining Culture
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-bold text-gray-700 mb-2">What You'll Miss:</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li>• Mexican food (limited in Ireland)</li>
                      <li>• Asian food variety (less diverse)</li>
                      <li>• In-N-Out Burger</li>
                      <li>• Trader Joe's</li>
                      <li>• Year-round fresh produce</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-700 mb-2">What You'll Love:</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li>• Amazing pub culture</li>
                      <li>• Fresh seafood everywhere</li>
                      <li>• Quality Irish beef and lamb</li>
                      <li>• Artisan food markets</li>
                      <li>• European food imports</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-2xl font-bold text-[#0A381F] mb-4">
                  <i className="ri-time-line text-[#D4AF37] mr-2"></i>
                  Work Culture & Pace
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-bold text-gray-700 mb-2">California Tech Culture:</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li>• "Hustle culture" mentality</li>
                      <li>• Long hours expected</li>
                      <li>• 10-15 vacation days</li>
                      <li>• Always-on email culture</li>
                      <li>• Work defines identity</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-700 mb-2">Ireland Work Culture:</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li>• Work-life balance valued</li>
                      <li>• Standard 40-hour weeks</li>
                      <li>• 20-25 vacation days</li>
                      <li>• Respect for personal time</li>
                      <li>• Life outside work matters</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Practical Tips */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-[#0A381F] mb-8">Practical Tips for Californians</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6">
                <h3 className="text-xl font-bold text-[#0A381F] mb-3">
                  <i className="ri-flight-takeoff-line text-blue-600 mr-2"></i>
                  Flights from California
                </h3>
                <p className="text-gray-700 mb-3">
                  <strong>Direct flights:</strong> SFO and LAX to Dublin (10-11 hours)
                </p>
                <p className="text-gray-700 mb-3">
                  <strong>Airlines:</strong> Aer Lingus, United
                </p>
                <p className="text-gray-700 mb-3">
                  <strong>Cost:</strong> $500-1,200 roundtrip (book 2-3 months ahead)
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Pro tip:</strong> Red-eye flights from CA arrive in Ireland morning - perfect timing!
                </p>
              </div>

              <div className="bg-green-50 border-l-4 border-green-500 p-6">
                <h3 className="text-xl font-bold text-[#0A381F] mb-3">
                  <i className="ri-car-line text-green-600 mr-2"></i>
                  Driving in Ireland
                </h3>
                <p className="text-gray-700 mb-3">
                  <strong>Big change:</strong> They drive on the LEFT side!
                </p>
                <p className="text-gray-700 mb-3">
                  <strong>Your CA license:</strong> Valid for 12 months
                </p>
                <p className="text-gray-700 mb-3">
                  <strong>After 12 months:</strong> Exchange for Irish license (no test needed)
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Pro tip:</strong> Many Californians find they don't need a car in Dublin - public transport is excellent!
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6">
                <h3 className="text-xl font-bold text-[#0A381F] mb-3">
                  <i className="ri-phone-line text-yellow-600 mr-2"></i>
                  Your California Phone Number
                </h3>
                <p className="text-gray-700 mb-3">
                  Port your CA number to Google Voice (free) to keep it for US banking and 2FA.
                </p>
                <p className="text-gray-700 mb-3">
                  Get an Irish SIM card (€20-40/month) - much cheaper than AT&T/Verizon international plans.
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Pro tip:</strong> Irish carriers: Vodafone, Three, Eir. All offer unlimited data plans.
                </p>
              </div>

              <div className="bg-purple-50 border-l-4 border-purple-500 p-6">
                <h3 className="text-xl font-bold text-[#0A381F] mb-3">
                  <i className="ri-bank-card-line text-purple-600 mr-2"></i>
                  Banking & Money
                </h3>
                <p className="text-gray-700 mb-3">
                  Keep your US bank account (Wells Fargo, Chase, BofA) and open an Irish account.
                </p>
                <p className="text-gray-700 mb-3">
                  Use Wise or Revolut for currency transfers - avoid high bank fees.
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Pro tip:</strong> Get a no-foreign-transaction-fee credit card before leaving CA.
                </p>
              </div>
            </div>
          </section>

          {/* California Expat Community */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-[#0A381F] mb-8">California Expat Community in Ireland</h2>
            <div className="bg-[#0A381F] text-white p-8 rounded-lg">
              <p className="text-lg mb-6">
                There's a huge California expat community in Ireland, especially in Dublin and Cork. Many organize meetups, watch 49ers/Lakers games together, and help newcomers adjust to Irish life.
              </p>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-[#D4AF37] mb-4">Where Californians Gather:</h3>
                  <ul className="space-y-2">
                    <li>• American expat Facebook groups</li>
                    <li>• Tech meetups (lots of ex-Bay Area folks)</li>
                    <li>• Sports bars showing NFL/NBA games</li>
                    <li>• Stanford/Berkeley/UCLA alumni groups</li>
                    <li>• American Chamber of Commerce events</li>
                    <li>• California-themed restaurants and bars</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#D4AF37] mb-4">Things You Can Find:</h3>
                  <ul className="space-y-2">
                    <li>• Mexican food (limited but exists!)</li>
                    <li>• American grocery items at specialty stores</li>
                    <li>• Sports bars showing CA teams</li>
                    <li>• American-style coffee shops</li>
                    <li>• Amazon.ie delivers to Ireland</li>
                    <li>• Most US streaming services work</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <div className="bg-gradient-to-r from-[#0A381F] to-[#0d4a28] text-white rounded-lg p-12 text-center">
            <h2 className="text-4xl font-bold mb-4">Ready to Trade California Sun for Irish Green?</h2>
            <p className="text-xl mb-8 opacity-90">
              Join thousands of Californians who've made the move. Get personalized guidance for your relocation from the Golden State to the Emerald Isle.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/directory" 
                className="bg-[#D4AF37] text-[#0A381F] px-10 py-4 rounded-lg font-bold text-lg hover:bg-[#c49d2f] transition-colors whitespace-nowrap"
              >
                Find Relocation Services
              </Link>
              <Link 
                to="/tools/cost-calculator" 
                className="bg-white text-[#0A381F] px-10 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                Calculate Your Costs
              </Link>
              <button 
                onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
                className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-[#0A381F] transition-colors whitespace-nowrap"
              >
                Ask Our AI Assistant
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}